import Header from "@/components/Header";
import Footer from "@/components/Footer";
import TutorialPricingSection from "@/components/TutorialPricingSection";
import { Download, Key, AlertCircle, Play } from "lucide-react";
import { Helmet } from "react-helmet-async";
import tutorialImage from "@/assets/installation-iptv-smart-tv-samsung-lg.jpg";
import { useAffiliateTracking } from "@/hooks/useAffiliateTracking";

const IptvSmartTvAndroid = () => {
  useAffiliateTracking();
  const alternativeLinks = [
    "http://az.dmsmarters2.com",
    "http://d875.dtv.homes:8080",
    "http://dm.pixehd.me:2082",
    "http://dm.opixtv.com:2082",
    "http://dreamsmart.me:2082",
    "http://chrf.25atg09.6g.in"
  ];

  return (
    <>
      <Helmet>
        <title>Tutoriel IPTV Smart TV Android (Samsung, LG, Hisense, Sony) | RoiDuStreaming</title>
        <meta name="description" content="Guide complet pour installer et configurer l'IPTV sur Smart TV Android (Samsung, LG, Hisense, Sony) avec IPTV Smarters Pro. Tutoriel étape par étape." />
        <meta name="keywords" content="IPTV Smart TV, IPTV Samsung, IPTV LG, IPTV Hisense, IPTV Sony, Smarters Pro, tutoriel IPTV TV" />
        <link rel="canonical" href="https://roidustreaming.fr/tutoriels/iptv-smart-tv-android" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="relative min-h-[50vh] flex items-center justify-center px-4 py-20 hero-bg">
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 to-background"></div>
          <div className="relative z-10 max-w-5xl mx-auto text-center">
            <span className="text-7xl mb-6 block">📺</span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 gradient-text">
              Tutoriel IPTV sur Smart TV Android
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground">
              Samsung, LG, Hisense, Sony – Installation avec IPTV Smarters Pro
            </p>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            
            {/* Image Smart TV */}
            <div className="mb-12 rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={tutorialImage} 
                alt="Smart TV Android pour tutoriel IPTV" 
                className="w-full h-auto object-cover"
              />
            </div>

            {/* Étape 1 */}
            <div className="card-premium p-8 mb-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <Download className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">1️⃣ Installer IPTV Smarters Pro</h2>
              </div>
              <ul className="space-y-3 text-foreground/90">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Accède à la section <strong>Apps</strong> de ta Smart TV.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Clique sur la <strong>loupe</strong> (recherche) en haut à droite.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Recherche « <strong>IPTV Smarters Pro</strong> ».</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Sélectionne et installe l'application.</span>
                </li>
                <li className="flex items-start gap-2 mt-4 p-4 bg-muted/50 rounded-lg">
                  <span className="text-primary">💡</span>
                  <span className="text-sm">
                    Si vous connaissez déjà une application IPTV qui fonctionne bien sur votre appareil, vous pouvez l'utiliser.
                  </span>
                </li>
              </ul>
            </div>

            {/* Étape 2 */}
            <div className="card-premium p-8 mb-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <Play className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">2️⃣ Lancer l'application</h2>
              </div>
              <ul className="space-y-3 text-foreground/90">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Ouvre <strong>IPTV Smarters Pro</strong>.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Accepte tous les dialogues qui s'affichent.</span>
                </li>
              </ul>
              <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                <p className="text-foreground/90 font-medium mb-2">⚠️ En cas de problème :</p>
                <ul className="space-y-2 text-foreground/80">
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>Redémarre ta TV.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>Vérifie que ta Smart TV est bien mise à jour.</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Étape 3 */}
            <div className="card-premium p-8 mb-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <Key className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">3️⃣ Entrer les identifiants IPTV</h2>
              </div>
              <ul className="space-y-3 text-foreground/90">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span><strong>Nom du profil</strong> : choisis un nom (ex. Mon IPTV).</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span><strong>Nom d'utilisateur</strong> : celui fourni par RoiDuStreaming.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span><strong>Mot de passe</strong> : celui fourni par RoiDuStreaming.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span><strong>URL du serveur</strong> : celle fournie par RoiDuStreaming.</span>
                </li>
              </ul>
              <div className="mt-6 p-4 bg-primary/10 rounded-lg border border-primary/20">
                <p className="text-foreground font-medium">
                  ✔️ Vérifie bien les données, puis clique sur « <strong>ADD USER</strong> ».
                </p>
              </div>
            </div>

            {/* Étape 4 - Dépannage */}
            <div className="card-premium p-8 mb-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">4️⃣ Conseils & Dépannage</h2>
              </div>
              
              <div className="mb-6">
                <p className="text-foreground/90 mb-4">
                  👉 <strong>Si le premier lien ne fonctionne pas</strong>, essaie l'un de ceux-ci :
                </p>
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  {alternativeLinks.map((link, index) => (
                    <code key={index} className="block text-sm text-primary font-mono">
                      {index + 1}. {link}
                    </code>
                  ))}
                </div>
              </div>

              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-foreground/90">
                  👉 <strong>Si les films/séries ne s'affichent pas</strong> :
                </p>
                <ul className="mt-2 space-y-2 text-foreground/80">
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>Relance l'application.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>Si le problème persiste, essaie un autre lien parmi ceux listés ci-dessus.</span>
                  </li>
                </ul>
              </div>
            </div>

          </div>
        </section>

        {/* Pricing Section */}
        <TutorialPricingSection />
        
      </div>
    </>
  );
};

export default IptvSmartTvAndroid;
